"use strict";
/**
 * Gaia Tag Parser
 *
 * Parses self-closing <Gaia /> placeholder tags in agent markdown text,
 * replacing them with unique placeholders before markdown processing,
 * then resolving those placeholders into rendered HTML (content links, etc.)
 * after markdown-to-HTML conversion.
 *
 * Tags carry only `id` and `text` attributes. The `id` maps to a document's
 * `componentId`, and the document's `componentType` drives rendering dispatch.
 *
 * Mirrors the LaTeX extract-placeholder-replace pattern from
 * lib/services/markdown/index.js but scoped to Oracle-specific logic.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderOracleMessageHtml = exports.buildDocumentMap = exports.renderGaiaPlaceholders = exports.extractGaiaTags = void 0;
var markdown_1 = require("../../services/markdown");
// Placeholder prefix — uses a rare Unicode character (Ğ) to avoid collisions
var PLACEHOLDER_PREFIX = 'ĞĞ';
// Matches <Gaia ... /> (self-closing) and <Gaia ...> (non-self-closing) tags.
// The LLM produces both variants, so the trailing slash is optional.
var GAIA_TAG_REGEX = /<Gaia\s+([^>]*?)\s*\/?>/g;
// Matches individual key="value" attribute pairs
var ATTRIBUTE_REGEX = /(\w+)="([^"]*)"/g;
/**
 * Parses attributes from a Gaia tag's attribute string.
 * Handles attributes in any order.
 */
var parseAttributes = function (attrString) {
    var attrs = {};
    var match = ATTRIBUTE_REGEX.exec(attrString);
    while (match) {
        var key = match[1], value = match[2];
        attrs[key] = value;
        match = ATTRIBUTE_REGEX.exec(attrString);
    }
    return attrs;
};
/**
 * Extracts <Gaia /> tags from text, replacing each with a unique placeholder.
 *
 * @param text - Raw markdown text potentially containing <Gaia /> tags
 * @returns processedText with placeholders, and tagDict mapping placeholders to parsed tag data
 */
var extractGaiaTags = function (text) {
    var tagDict = {};
    var counter = 0;
    var processedText = text.replace(GAIA_TAG_REGEX, function (_match, attrString) {
        var attrs = parseAttributes(attrString);
        var id = attrs.id || '';
        var tagText = attrs.text || '';
        var key = "GAIA_".concat(counter);
        counter += 1;
        var placeholder = "".concat(PLACEHOLDER_PREFIX).concat(key).concat(PLACEHOLDER_PREFIX);
        tagDict[placeholder] = { id: id, text: tagText };
        return placeholder;
    });
    return { processedText: processedText, tagDict: tagDict };
};
exports.extractGaiaTags = extractGaiaTags;
/**
 * Renders a content link as an anchor tag.
 * If the content link data is available, renders a clickable link.
 * Otherwise renders a pending span with fallback text.
 */
var renderContentLink = function (tag, contentLink, sameTab) {
    if (sameTab === void 0) { sameTab = false; }
    if (contentLink) {
        var linkText = tag.text || contentLink.title;
        var targetAttr = sameTab ? '' : ' target="_blank" rel="noopener noreferrer"';
        return "<a href=\"/".concat(contentLink.path, "\" class=\"oracle-content-link\" data-content-id=\"").concat(contentLink.contentId, "\"").concat(targetAttr, ">").concat(linkText, "</a>");
    }
    // Fallback: content link data not available
    var fallbackText = tag.text || 'Loading...';
    return "<span class=\"oracle-content-link--pending\">".concat(fallbackText, "</span>");
};
/**
 * Replaces Gaia tag placeholders in post-markdown HTML with rendered content.
 * Looks up each tag's document by id and dispatches rendering based on
 * the document's componentType.
 *
 * @param html - HTML string after markdown processing, containing placeholders
 * @param tagDict - Map of placeholders to parsed tag data
 * @param documentMap - Map of componentId to document entry (componentType + type-specific data)
 * @returns HTML with placeholders resolved to rendered content
 */
var renderGaiaPlaceholders = function (html, tagDict, documentMap, sameTab) {
    if (sameTab === void 0) { sameTab = false; }
    var result = html;
    Object.entries(tagDict).forEach(function (_a) {
        var placeholder = _a[0], tag = _a[1];
        var doc = documentMap.get(tag.id);
        var rendered;
        switch (doc === null || doc === void 0 ? void 0 : doc.componentType) {
            case 'contentLink':
                rendered = renderContentLink(tag, doc.contentLink, sameTab);
                break;
            default:
                // No matching document or unknown componentType: render fallback text
                rendered = tag.text || '';
                break;
        }
        result = result.split(placeholder).join(rendered);
    });
    return result;
};
exports.renderGaiaPlaceholders = renderGaiaPlaceholders;
/**
 * Builds a DocumentMap from raw GraphQL-shaped documents, filtered to only
 * include documents referenced by Gaia tags in the text.
 *
 * @param documents - Array of documents from GraphQL (subscription or query)
 * @param tagDict - Parsed Gaia tag dictionary from extractGaiaTags
 * @returns Map of componentId → DocumentEntry for placeholder resolution
 */
var buildDocumentMap = function (documents, tagDict) {
    var map = new Map();
    if (!documents)
        return map;
    var referencedIds = new Set(Object.values(tagDict).map(function (tag) { return tag.id; }));
    documents.forEach(function (doc) {
        if (doc.componentId && referencedIds.has(doc.componentId)) {
            var entry = { componentType: doc.componentType || '' };
            if (doc.componentType === 'contentLink' && doc.contentLink) {
                entry.contentLink = {
                    contentType: doc.contentLink.contentType || '',
                    contentId: doc.contentLink.contentId || 0,
                    title: doc.contentLink.title || '',
                    path: doc.contentLink.path || '',
                };
            }
            map.set(doc.componentId, entry);
        }
    });
    return map;
};
exports.buildDocumentMap = buildDocumentMap;
/**
 * Full rendering pipeline for Oracle messages with Gaia tags.
 *
 * Encapsulates: extractGaiaTags → oracleMarkdownToHtml → buildDocumentMap → renderGaiaPlaceholders
 *
 * @param markdown - Raw markdown text potentially containing <Gaia /> tags
 * @param sameTab - When true, links open in the same tab
 * @param documents - Array of documents from GraphQL for resolving Gaia tag content links
 * @returns Rendered HTML string
 */
var renderOracleMessageHtml = function (_a) {
    var markdown = _a.markdown, _b = _a.sameTab, sameTab = _b === void 0 ? false : _b, documents = _a.documents;
    var _c = (0, exports.extractGaiaTags)(markdown || ''), processedText = _c.processedText, tagDict = _c.tagDict;
    var htmlContent = (0, markdown_1.oracleMarkdownToHtml)({ markdown: processedText, sameTab: sameTab });
    var htmlString = Array.isArray(htmlContent) ? htmlContent.join('') : htmlContent || '';
    if (Object.keys(tagDict).length > 0) {
        var documentMap = (0, exports.buildDocumentMap)(documents, tagDict);
        htmlString = (0, exports.renderGaiaPlaceholders)(htmlString, tagDict, documentMap, sameTab);
    }
    return htmlString;
};
exports.renderOracleMessageHtml = renderOracleMessageHtml;
